/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [true]
*/

import {
    DropDown,
    getFilterElementsDefault,
    useErrorState,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addOutlet,
    changeOutlet,
    deleteOutlet,
    getOutlet,
    getOutletsExcel,
    getOutletsList,
} from 'axios/outletsApi';
import { getTradeOrgsList } from 'axios/tradeOrgsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import ButtonExcelFileUpload from 'components/ExcelFileUpload/ButtonExcelFileUpload';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    Outlet,
    OutletAdd,
    OutletChange,
    OutletFilter as OutletFilterType,
    OutletTable,
} from 'models/api/outlet';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { checkboxFormatter, deleteActionBtnFormatter } from 'utils/columnFormatters';
import { UploadResult } from 'utils/fileUploadNotificator';
import { getSelectList } from '../../../axios/dictionaryApi';
import OutletsChangeModal from './Modal/OutletChangeModal';
import OutletAddButton from './OutletAddButton';
import OutletFilter from './OutletsFilter';

const Outlets = () => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [merchantRegions, setMerchantRegions] = useState<DropDown[]>([]);
    const [typeWorks, setTypeWorks] = useState<DropDown[]>([]);
    const { hasRole } = useAuth();
    const { handleRequestError } = useErrorState();

    const { isFetching: tradeOrgsFetching, data: tradeOrgs } = useQuery(
        ['tradeOrgs'],
        () => getTradeOrgsList(api),
        {
            onError: handleRequestError,
        }
    );

    const { isFetching: isOutletDictsFetching } = useQuery(
        ['outletDicts', i18n.language],
        () => getSelectList(api, ['MerchantRegions', 'TypeWorks']),
        {
            onSuccess: (data) => {
                setMerchantRegions(data['MerchantRegions'] || []);
                setTypeWorks(data['TypeWorks'] || []);
            },
            onError: (err) => handleRequestError(err),
            enabled: !merchantRegions.length,
        }
    );
    const { data: brandCodeTypeResponse, isFetched: isFetchingBrandCodeTypes } = useQuery(
        'brandCode',
        () => getSelectList(api, ['BrandCodeType']),
        {
            onError: (err) => handleRequestError(err),
            enabled: hasRole('merchantBrandCodeType'),
        }
    );
    const brandCode = useMemo<DropDown[]>(() => {
        return brandCodeTypeResponse?.['BrandCodeType'] ?? [];
    }, [brandCodeTypeResponse]);

    const uploadCallback = (res: UploadResult) => {
        (res === 'success' || res === 'warning') && queryClient.invalidateQueries([['outlets']]);
    };

    const buttons = useMemo(() => {
        const result = [];

        if (hasRole('outletsUpload')) {
            result.push(
                <ButtonExcelFileUpload key="2" controller="outlets" callback={uploadCallback} />
            );
        }

        if (hasRole('outletsCreate')) {
            result.push(<OutletAddButton key="1" showModal={() => setIsModalVisible(true)} />);
        }

        return result;
    }, [hasRole]);

    return (
        <BOWorkplace<OutletTable, Outlet, OutletFilterType, OutletChange, OutletAdd>
            testId="Outlets"
            pageTitle="BackOfficeOutlets"
            filterParams={{}}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey="BackOfficeId" />,
                    width: 70,
                    minWidth: 70,
                },
                {
                    key: 'isActive',
                    name: <Trans i18nKey="BackOfficeIsActive" />,
                    cellClass: 'cell-centered',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    formatter: checkboxFormatter('isActive'),
                },
                { key: 'code', name: <Trans i18nKey="BackOfficeCode" /> },
                { key: 'tradeOrgName', name: <Trans i18nKey="BackOfficeTradeOrg" /> },
                { key: 'typeWork', name: <Trans i18nKey="BackOfficeTypeWork" /> },
                { key: 'region', name: <Trans i18nKey="BackOfficeRegion" /> },
                { key: 'address', name: <Trans i18nKey="BackOfficeAddress" /> },
                { key: 'lat', name: <Trans i18nKey="BackOfficeLatitude" /> },
                { key: 'lng', name: <Trans i18nKey="BackOfficeLongitude" /> },
                { key: 'dsc', name: <Trans i18nKey="BackOfficeDescription" /> },
                {
                    key: 'actions',
                    name: <FontAwesomeIcon icon={faBoltLightning} />,
                    cellClass: 'cell-centered no-change-modal',
                    headerCellClass: 'cell-centered',
                    width: 70,
                    minWidth: 70,
                    draggable: false,
                    resizable: false,
                    clickDisable: true,
                    formatter: deleteActionBtnFormatter(hasRole('outletsDelete')),
                },
            ]}
            storagePath="Outlets/columns"
            getFilterElements={getFilterElementsDefault([
                { id: 'id', name: t('BackOfficeId') },
                { id: 'address', name: t('BackOfficeAddress') },
                {
                    id: 'tradeOrgId',
                    formatter: (val) => {
                        const source = (tradeOrgs || []).find((i) => i.value === val.toString());
                        return source ? `${t('BackOfficeTradeOrg')}: ${source.text}` : '';
                    },
                },
            ])}
            showModal={hasRole('outletsGetById') ? () => setIsModalVisible(true) : undefined}
            buttons={buttons}
            filter={
                <OutletFilter
                    tradeOrgs={tradeOrgs || []}
                    isTradeOrgsFetching={tradeOrgsFetching}
                    merchantRegions={merchantRegions}
                    typeWorks={typeWorks}
                    isOutletDictsFetching={isOutletDictsFetching}
                />
            }
            modal={{
                component: isModalVisible && (
                    <OutletsChangeModal
                        isOpen={isModalVisible}
                        toggle={setIsModalVisible}
                        merchantRegions={merchantRegions}
                        typeWorks={typeWorks}
                        isOutletDictsFetching={isOutletDictsFetching}
                        brandCode={brandCode}
                        isFetchingBrandCodeTypes={isFetchingBrandCodeTypes}
                    />
                ),
                fetchCurrentItemQueryFn: (id) => getOutlet(api, id),
                changeMutationFn: (item) => changeOutlet(api, item),
                addMutationFn: (item) => addOutlet(api, item),
            }}
            deleteMutationFn={(id) => deleteOutlet(api, id)}
            fetchQueryFn={(filter) => getOutletsList(api, filter)}
            queryKey={['outlets']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getOutletsExcel(api, filter)}
            showExcelBtn={hasRole('outletsExcel')}
        />
    );
};

const useOutletWorkplaceState = () =>
    useWorkplaceState<OutletTable, Outlet, OutletChange, OutletAdd>();

export default Outlets;
export { useOutletWorkplaceState };
